import React, { useEffect, useState } from "react";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import { CheckSquare, Flag, Calendar, User } from "lucide-react";

const priorityStyles = {
  low: "bg-slate-100 text-slate-600 border-slate-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  high: "bg-red-100 text-red-600 border-red-200",
};

const statusStyles = {
  todo: { card: "border-l-slate-300", badge: "bg-slate-100 text-slate-600", label: "Todo" },
  in_progress: { card: "border-l-[#38b6e8]", badge: "bg-sky-100 text-sky-700", label: "In Progress" },
  done: { card: "border-l-emerald-400", badge: "bg-emerald-100 text-emerald-700", label: "Done" },
};

export default function TaskBoard() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tasks").then((r) => setTasks(r.data)).finally(() => setLoading(false));
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/tasks/${id}/status`, { status });
      setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
      toast.success("Task status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const tabs = [
    { key: "all", label: "All Tasks" },
    { key: "todo", label: "Todo" },
    { key: "in_progress", label: "In Progress" },
    { key: "done", label: "Done" },
  ];

  const filtered = filter === "all" ? tasks : tasks.filter((t) => t.status === filter);
  const pending = tasks.filter((t) => t.status !== "done").length;

  return (
    <Layout>
      <div className="p-8 page-enter">
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-slate-900">My Tasks</h1>
          <p className="text-slate-500 text-sm mt-1">{pending} active • {tasks.filter((t) => t.status === "done").length} completed</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-slate-100 p-1 rounded-lg w-fit">
          {tabs.map((t) => {
            const count = t.key === "all" ? tasks.length : tasks.filter((x) => x.status === t.key).length;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  filter === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`task-filter-${t.key}`}
              >
                {t.label} <span className="ml-1 text-xs opacity-70">({count})</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 h-40 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-16 text-center shadow-sm">
            <CheckSquare size={48} className="text-slate-200 mx-auto mb-4" />
            <p className="text-slate-500 font-semibold text-lg">No tasks assigned</p>
            <p className="text-slate-400 text-sm mt-1">You're all caught up!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="task-board">
            {filtered.map((task) => {
              const st = statusStyles[task.status];
              return (
                <div
                  key={task.id}
                  className={`bg-white rounded-xl border border-slate-200 shadow-sm p-5 border-l-4 ${st.card} hover:shadow-md transition-all`}
                  data-testid={`task-card-${task.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-semibold text-slate-800 text-sm leading-snug">{task.title}</h3>
                    <Badge className={`${st.badge} text-xs font-semibold shrink-0`}>{st.label}</Badge>
                  </div>
                  {task.description && (
                    <p className="text-slate-500 text-xs mb-3 leading-relaxed">{task.description}</p>
                  )}
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <Badge className={`${priorityStyles[task.priority]} border text-xs`}>
                      <Flag size={10} className="mr-1" />{task.priority}
                    </Badge>
                    {task.due_date && (
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar size={11} />Due {task.due_date}
                      </span>
                    )}
                  </div>
                  {task.status !== "done" && (
                    <div className="pt-3 border-t border-slate-100">
                      <Select value={task.status} onValueChange={(v) => handleStatusChange(task.id, v)}>
                        <SelectTrigger
                          className="h-8 text-xs border-slate-200 focus:ring-[#38b6e8] bg-slate-50"
                          data-testid={`update-status-${task.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todo">Todo</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="done">Done</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
